import { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Stack, TextField, Typography } from '@mui/material'
import { useConferirPagerEmUsoMutation } from '../../api'

const MAX_DIGITOS = 4

interface PassoPagerProps {
  valor: string
  onChange: (valor: string) => void
  minimo: number | null
  maximo: number | null
  onConferenciaChange: (conferido: boolean) => void
}

function rotuloFaixa(minimo: number | null, maximo: number | null): string | null {
  if (minimo != null && maximo != null) return `de ${minimo} a ${maximo}`
  if (minimo != null) return `a partir de ${minimo}`
  if (maximo != null) return `ate ${maximo}`

  return null
}

export function PassoPager({ valor, onChange, minimo, maximo, onConferenciaChange, }: PassoPagerProps): JSX.Element {
  const [conferir, { isLoading }] = useConferirPagerEmUsoMutation()
  const [emUso, setEmUso] = useState<{ pedido: string | null; desdeMinutos: number | null } | null>(null)
  const [confirmadoMesmoAssim, setConfirmadoMesmoAssim] = useState(false)
  const numero = Number(valor)
  const preenchido = valor !== '' && numero > 0
  const faixa = rotuloFaixa(minimo, maximo)
  const foraDaFaixa = preenchido && ((minimo != null && numero < minimo) || (maximo != null && numero > maximo))

  const digitar = useCallback(
    (bruto: string) => {
      setEmUso(null)
      setConfirmadoMesmoAssim(false)
      onChange(bruto.replace(/\D/g, '').slice(0, MAX_DIGITOS))
    },
    [onChange],
  )

  const conferirAgora = useCallback(async () => {
    if (!preenchido || foraDaFaixa) return

    try {
      const r = await conferir({ numero }).unwrap()
      setEmUso(r.emUso ? { pedido: r.pedido, desdeMinutos: r.desdeMinutos } : null)
    } catch {
      setEmUso(null)
    }
  }, [preenchido, foraDaFaixa, numero, conferir])

  useEffect(() => {
    if (!preenchido || foraDaFaixa) return

    const timer = setTimeout(() => void conferirAgora(), 500)
    return () => clearTimeout(timer)
  }, [preenchido, foraDaFaixa, conferirAgora])

  useEffect(() => {
    if (!preenchido || foraDaFaixa) {
      onConferenciaChange(false)
      return
    }

    onConferenciaChange(!emUso || confirmadoMesmoAssim)
  }, [preenchido, foraDaFaixa, emUso, confirmadoMesmoAssim, onConferenciaChange])

  return (
    <Stack spacing={2} data-form-teclado>
      <Typography variant="subtitle2" color="text.secondary">
        Qual o numero do seu pager?
      </Typography>

      <TextField
        label="Número do pager"
        value={valor}
        onChange={(e) => digitar(e.target.value)}
        fullWidth
        required
        autoFocus
        error={foraDaFaixa}
        helperText={
          foraDaFaixa
            ? `Esta loja usa pagers ${faixa}. Confira o número no aparelho.`
            : `É o número do aparelho que voce pegou no balção.${faixa ? ` A numeração vai ${faixa}.` : ''}`
        }
        inputProps={{ inputMode: 'numeric', enterKeyHint: 'done', maxLength: MAX_DIGITOS }}
      />

      {emUso && !confirmadoMesmoAssim && (
        <Alert
          severity="warning"
          action={
            <Box
              component="button"
              type="button"
              onClick={() => setConfirmadoMesmoAssim(true)}
              sx={{
                border: 0,
                bgcolor: 'transparent',
                color: 'inherit',
                font: 'inherit',
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              E esse mesmo
            </Box>
          }
        >
          O pager {valor} parece estar em uso
          {emUso.desdeMinutos != null ? ` ha ${emUso.desdeMinutos} min` : ''}
          {emUso.pedido ? ` (pedido ${emUso.pedido})` : ''}. Confira o numero no aparelho.
        </Alert>
      )}

      {emUso && confirmadoMesmoAssim && (
        <Alert severity="info">
          Ok, seguindo com o pager {valor}.
        </Alert>
      )}

      {isLoading && (
        <Typography variant="caption" color="text.secondary">
          Conferindo...
        </Typography>
      )}
    </Stack>
  )
}
